import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../../../components/dialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import {
  AppAutocomplete,
  AppButton,
  AppIconButton,
  AppTextField,
  AppToggleField,
} from "../../../components/form";
import {
  AppFormField,
  AppFormGrid,
  AppSlideOver,
  PageCommandBar,
  PageSectionCard,
  PageStatCard,
  ResponsiveActions,
} from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import VpnProfileSelect from "../../../components/VpnProfileSelect";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { useConfirm } from "material-ui-confirm";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import {
  openapiCatalogService,
  type OpenApiCatalog,
  type OpenApiCatalogAuthType,
  type OpenApiCatalogOperation,
  type OpenApiCatalogSavePayload,
  type OpenApiOAuthGrant,
} from "../../../services/openapiCatalogService";
import ResourceQueryStatsPanel, {
  formatQueryStatsCaption,
  type ResourceQueryCallRow,
} from "../../../components/ResourceQueryStatsPanel";

type ViewMode = "browse" | "editor";

/** Platform default when no authorization feature is attached. */
const DEFAULT_AUTH: OpenApiCatalogAuthType = "TOKEN_PASS";

type SecretFlags = {
  fixedTokenSet: boolean;
  basicPasswordSet: boolean;
  oauthClientSecretSet: boolean;
  oauthPasswordSet: boolean;
};

type AuthDraft = {
  authType: OpenApiCatalogAuthType;
  oauthGrant: OpenApiOAuthGrant;
  basicUsername: string;
  oauthTokenUrl: string;
  oauthClientId: string;
  oauthScopes: string;
  oauthUsername: string;
  fixedToken: string;
  basicPassword: string;
  oauthClientSecret: string;
  oauthPassword: string;
};

type CatalogDraft = {
  id?: string;
  name: string;
  openapiDocUrl: string;
  baseUrl: string;
  enabled: boolean;
  /** "" = no VPN. */
  vpnProfileId: string;
  vpnProfileName?: string | null;
  autoDisableEnabled: boolean;
  minSuccessPercentText: string;
  minCallsText: string;
} & AuthDraft;

const AUTH_FEATURE_TYPES: OpenApiCatalogAuthType[] = [
  "FIXED_TOKEN",
  "BASIC",
  "OAUTH",
  "NONE",
];

function isAuthFeatureAttached(authType?: OpenApiCatalogAuthType | null): boolean {
  return !!authType && authType !== DEFAULT_AUTH;
}

function emptyAuthDraft(authType: OpenApiCatalogAuthType = "FIXED_TOKEN"): AuthDraft {
  return {
    authType,
    oauthGrant: "CLIENT_CREDENTIALS",
    basicUsername: "",
    oauthTokenUrl: "",
    oauthClientId: "",
    oauthScopes: "",
    oauthUsername: "",
    fixedToken: "",
    basicPassword: "",
    oauthClientSecret: "",
    oauthPassword: "",
  };
}

function emptyDraft(): CatalogDraft {
  return {
    name: "",
    openapiDocUrl: "",
    baseUrl: "",
    enabled: true,
    vpnProfileId: "",
    autoDisableEnabled: false,
    minSuccessPercentText: "80",
    minCallsText: "10",
    ...emptyAuthDraft(DEFAULT_AUTH),
  };
}

function emptySecrets(): SecretFlags {
  return {
    fixedTokenSet: false,
    basicPasswordSet: false,
    oauthClientSecretSet: false,
    oauthPasswordSet: false,
  };
}

function secretsFromCatalog(catalog: OpenApiCatalog): SecretFlags {
  return {
    fixedTokenSet: !!catalog.fixedTokenSet,
    basicPasswordSet: !!catalog.basicPasswordSet,
    oauthClientSecretSet: !!catalog.oauthClientSecretSet,
    oauthPasswordSet: !!catalog.oauthPasswordSet,
  };
}

function draftFromCatalog(catalog: OpenApiCatalog): CatalogDraft {
  return {
    id: catalog.id,
    name: catalog.name || "",
    openapiDocUrl: catalog.openapiDocUrl || "",
    baseUrl: catalog.baseUrl || "",
    enabled: catalog.enabled !== false,
    vpnProfileId: catalog.vpnProfileId || "",
    vpnProfileName: catalog.vpnProfileName,
    autoDisableEnabled: !!catalog.autoDisableEnabled,
    minSuccessPercentText:
      catalog.autoDisableMinSuccessPercent != null ? String(catalog.autoDisableMinSuccessPercent) : "80",
    minCallsText: catalog.autoDisableMinCalls != null ? String(catalog.autoDisableMinCalls) : "10",
    authType: (catalog.authType || DEFAULT_AUTH) as OpenApiCatalogAuthType,
    oauthGrant: (catalog.oauthGrant || "CLIENT_CREDENTIALS") as OpenApiOAuthGrant,
    basicUsername: catalog.basicUsername || "",
    oauthTokenUrl: catalog.oauthTokenUrl || "",
    oauthClientId: catalog.oauthClientId || "",
    oauthScopes: catalog.oauthScopes || "",
    oauthUsername: catalog.oauthUsername || "",
    fixedToken: "",
    basicPassword: "",
    oauthClientSecret: "",
    oauthPassword: "",
  };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function authTypeLabel(t: Translate, authType: OpenApiCatalogAuthType): string {
  switch (authType) {
    case "FIXED_TOKEN":
      return t("openapiCatalogs.authFixedToken");
    case "BASIC":
      return t("openapiCatalogs.authBasic");
    case "OAUTH":
      return t("openapiCatalogs.authOauth");
    case "NONE":
      return t("openapiCatalogs.authNone");
    case "TOKEN_PASS":
    default:
      return t("openapiCatalogs.authTokenPass");
  }
}

function authSummary(t: Translate, draft: AuthDraft): string {
  switch (draft.authType) {
    case "FIXED_TOKEN":
      return t("openapiCatalogs.authSummaryFixedToken");
    case "BASIC":
      return draft.basicUsername
        ? t("openapiCatalogs.authSummaryBasic", { user: draft.basicUsername })
        : t("openapiCatalogs.authBasic");
    case "OAUTH":
      return draft.oauthGrant === "PASSWORD"
        ? t("openapiCatalogs.authSummaryOauthPassword", {
            clientId: draft.oauthClientId || "—",
          })
        : t("openapiCatalogs.authSummaryOauthClient", {
            clientId: draft.oauthClientId || "—",
          });
    case "NONE":
      return t("openapiCatalogs.authNoneHint");
    default:
      return t("openapiCatalogs.authFeatureDefaultHint");
  }
}

function snapshotChipColor(
  item: Pick<OpenApiCatalog, "hasSnapshot" | "lastError">
): "success" | "error" | "default" {
  if (item.hasSnapshot) return "success";
  if (item.lastError) return "error";
  return "default";
}

function ragChipColor(
  status?: OpenApiCatalog["ragStatus"]
): "success" | "warning" | "error" | "default" {
  if (status === "READY") return "success";
  if (status === "INGESTING" || status === "UPLOADED") return "warning";
  if (status === "FAILED") return "error";
  return "default";
}

function ragLabel(t: (key: string) => string, status?: OpenApiCatalog["ragStatus"]): string {
  if (status === "READY") return t("openapiCatalogs.ragReady");
  if (status === "INGESTING" || status === "UPLOADED") return t("openapiCatalogs.ragIngesting");
  if (status === "FAILED") return t("openapiCatalogs.ragFailed");
  return t("openapiCatalogs.ragPending");
}

function snapshotLabel(
  t: (key: string) => string,
  item: Pick<OpenApiCatalog, "hasSnapshot" | "lastError">
): string {
  if (item.hasSnapshot) return t("openapiCatalogs.hasSnapshot");
  if (item.lastError) return t("openapiCatalogs.fetchFailed");
  return t("openapiCatalogs.noSnapshot");
}

const softSurfaceSx = {
  px: 1.5,
  py: 1.25,
  borderRadius: 1.25,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === "dark"
      ? "0 1px 2px rgba(0,0,0,0.35)"
      : "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)",
};

const catalogTileSx = {
  ...softSurfaceSx,
  cursor: "pointer",
  transition: "border-color 120ms ease, box-shadow 120ms ease",
  "&:hover": { borderColor: "primary.light" },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

const methodOptionSx = (selected: boolean) => ({
  ...softSurfaceSx,
  cursor: "pointer",
  borderColor: selected ? "primary.main" : "divider",
  bgcolor: selected ? "action.selected" : "background.paper",
  transition: "border-color 120ms ease, background-color 120ms ease",
  "&:hover": {
    borderColor: selected ? "primary.main" : "primary.light",
  },
});

type AuthDialogProps = {
  open: boolean;
  editing: boolean;
  initial: AuthDraft;
  secrets: SecretFlags;
  catalogId?: string;
  onClose: () => void;
  onApply: (next: AuthDraft) => void;
};

function AuthorizationDialog({
  open,
  editing,
  initial,
  secrets,
  catalogId,
  onClose,
  onApply,
}: AuthDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<AuthDraft>(initial);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const patch = (p: Partial<AuthDraft>) => setForm((prev) => ({ ...prev, ...p }));
  const authType = form.authType;
  const oauthGrant = form.oauthGrant || "CLIENT_CREDENTIALS";
  const isPasswordGrant = authType === "OAUTH" && oauthGrant === "PASSWORD";

  const canApply = (() => {
    if (authType === "NONE") return true;
    if (authType === "FIXED_TOKEN") {
      return !!form.fixedToken?.trim() || (!!catalogId && secrets.fixedTokenSet);
    }
    if (authType === "BASIC") {
      const userOk = !!form.basicUsername?.trim();
      const pwdOk = !!form.basicPassword || (!!catalogId && secrets.basicPasswordSet);
      return userOk && pwdOk;
    }
    if (authType === "OAUTH") {
      const base =
        !!form.oauthTokenUrl?.trim() && !!form.oauthClientId?.trim();
      if (!base) return false;
      if (isPasswordGrant) {
        return (
          !!form.oauthUsername?.trim() &&
          (!!form.oauthPassword || (!!catalogId && secrets.oauthPasswordSet))
        );
      }
      return !!form.oauthClientSecret || (!!catalogId && secrets.oauthClientSecretSet);
    }
    return false;
  })();

  return (
    <AppSlideOver
      open={open}
      onClose={onClose}
      size="md"
      title={editing ? t("openapiCatalogs.authDialogTitleEdit") : t("openapiCatalogs.authDialogTitleAdd")}
      actions={
        <>
          <AppButton variant="outlined" onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          <AppButton disabled={!canApply} onClick={() => onApply(form)}>
            {t("openapiCatalogs.authDialogApply")}
          </AppButton>
        </>
      }
    >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("openapiCatalogs.authDialogHint")}
        </Typography>

        <Stack spacing={1}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {t("openapiCatalogs.authMethod")}
          </Typography>
          {AUTH_FEATURE_TYPES.map((type) => (
            <Box
              key={type}
              role="button"
              tabIndex={0}
              onClick={() =>
                patch({
                  authType: type,
                  ...(type !== "OAUTH" ? { oauthClientSecret: "", oauthPassword: "" } : {}),
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  patch({ authType: type });
                }
              }}
              sx={methodOptionSx(authType === type)}
            >
              <Typography variant="subtitle2" fontWeight={650}>
                {authTypeLabel(t, type)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {type === "FIXED_TOKEN" && t("openapiCatalogs.authSummaryFixedToken")}
                {type === "BASIC" && t("openapiCatalogs.authBasicDesc")}
                {type === "OAUTH" && t("openapiCatalogs.authOauthDesc")}
                {type === "NONE" && t("openapiCatalogs.authNoneHint")}
              </Typography>
            </Box>
          ))}
        </Stack>

        {authType === "FIXED_TOKEN" && (
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={
                  catalogId && secrets.fixedTokenSet
                    ? t("openapiCatalogs.fixedTokenKeep")
                    : t("openapiCatalogs.fixedToken")
                }
                type="password"
                value={form.fixedToken}
                onChange={(e) => patch({ fixedToken: e.target.value })}
                required={!catalogId || !secrets.fixedTokenSet}
                helperText={t("openapiCatalogs.secretKeepHint")}
                autoComplete="new-password"
                autoFocus
              />
            </AppFormField>
          </AppFormGrid>
        )}

        {authType === "BASIC" && (
          <AppFormGrid>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={t("openapiCatalogs.basicUsername")}
                value={form.basicUsername}
                onChange={(e) => patch({ basicUsername: e.target.value })}
                required
                autoFocus
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={
                  catalogId && secrets.basicPasswordSet
                    ? t("openapiCatalogs.basicPasswordKeep")
                    : t("openapiCatalogs.basicPassword")
                }
                type="password"
                value={form.basicPassword}
                onChange={(e) => patch({ basicPassword: e.target.value })}
                required={!catalogId || !secrets.basicPasswordSet}
                helperText={t("openapiCatalogs.secretKeepHint")}
                autoComplete="new-password"
              />
            </AppFormField>
          </AppFormGrid>
        )}

        {authType === "OAUTH" && (
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppToggleField
                label={t("openapiCatalogs.oauthGrant")}
                value={oauthGrant}
                onChange={(v) =>
                  patch({
                    oauthGrant: v as OpenApiOAuthGrant,
                    ...(v === "PASSWORD" ? { oauthClientSecret: "" } : {}),
                  })
                }
                options={[
                  {
                    value: "CLIENT_CREDENTIALS",
                    label: t("openapiCatalogs.oauthClientCredentials"),
                  },
                  { value: "PASSWORD", label: t("openapiCatalogs.oauthPasswordGrant") },
                ]}
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("openapiCatalogs.oauthTokenUrl")}
                value={form.oauthTokenUrl}
                onChange={(e) => patch({ oauthTokenUrl: e.target.value })}
                required
                helperText={t("openapiCatalogs.oauthTokenUrlHint")}
                autoFocus
              />
            </AppFormField>
            <AppFormField size={isPasswordGrant ? GRID_FULL : GRID_HALF}>
              <AppTextField
                label={t("openapiCatalogs.oauthClientId")}
                value={form.oauthClientId}
                onChange={(e) => patch({ oauthClientId: e.target.value })}
                required
              />
            </AppFormField>
            {!isPasswordGrant && (
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={
                    catalogId && secrets.oauthClientSecretSet
                      ? t("openapiCatalogs.oauthClientSecretKeep")
                      : t("openapiCatalogs.oauthClientSecret")
                  }
                  type="password"
                  value={form.oauthClientSecret}
                  onChange={(e) => patch({ oauthClientSecret: e.target.value })}
                  required={!catalogId || !secrets.oauthClientSecretSet}
                  helperText={t("openapiCatalogs.secretKeepHint")}
                  autoComplete="new-password"
                />
              </AppFormField>
            )}
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("openapiCatalogs.oauthScopes")}
                value={form.oauthScopes}
                onChange={(e) => patch({ oauthScopes: e.target.value })}
                helperText={t("openapiCatalogs.oauthScopesHint")}
              />
            </AppFormField>
            {isPasswordGrant && (
              <>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("openapiCatalogs.oauthUsername")}
                    value={form.oauthUsername}
                    onChange={(e) => patch({ oauthUsername: e.target.value })}
                    required
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={
                      catalogId && secrets.oauthPasswordSet
                        ? t("openapiCatalogs.oauthPasswordKeep")
                        : t("openapiCatalogs.oauthPassword")
                    }
                    type="password"
                    value={form.oauthPassword}
                    onChange={(e) => patch({ oauthPassword: e.target.value })}
                    required={!catalogId || !secrets.oauthPasswordSet}
                    helperText={t("openapiCatalogs.secretKeepHint")}
                    autoComplete="new-password"
                  />
                </AppFormField>
              </>
            )}
          </AppFormGrid>
        )}
    </AppSlideOver>
  );
}

export default function OpenApiCatalogsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();

  const [mode, setMode] = useState<ViewMode>("browse");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [opsLoading, setOpsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<OpenApiCatalog[]>([]);
  const [draft, setDraft] = useState<CatalogDraft>(emptyDraft);
  const [secrets, setSecrets] = useState<SecretFlags>(emptySecrets);
  const [live, setLive] = useState<OpenApiCatalog | null>(null);
  const [operations, setOperations] = useState<OpenApiCatalogOperation[]>([]);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [queryHistory, setQueryHistory] = useState<ResourceQueryCallRow[]>([]);
  const [queryHistoryPage, setQueryHistoryPage] = useState(0);
  const [queryHistoryTotal, setQueryHistoryTotal] = useState(0);
  const [queryHistoryLoading, setQueryHistoryLoading] = useState(false);
  const [tagAnalysesOpen, setTagAnalysesOpen] = useState(false);
  const [tagAnalysesLoading, setTagAnalysesLoading] = useState(false);
  const [tagAnalyses, setTagAnalyses] = useState<Array<{ tag: string; markdown?: string }>>([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [resettingStats, setResettingStats] = useState(false);

  const busy = saving || refreshing || analyzing;
  const authAttached = isAuthFeatureAttached(draft.authType);

  const loadList = useCallback(async () => {
    setError(null);
    const rows = await openapiCatalogService.listAdmin();
    setItems(rows);
    return rows;
  }, []);

  const pendingRagIds = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    const consider = (id?: string, status?: OpenApiCatalog["ragStatus"]) => {
      if (!id || seen.has(id)) return;
      if (status === "INGESTING" || status === "UPLOADED") {
        seen.add(id);
        ids.push(id);
      }
    };
    consider(live?.id, live?.ragStatus);
    for (const item of items) consider(item.id, item.ragStatus);
    return ids;
  }, [live?.id, live?.ragStatus, items]);

  useEffect(() => {
    if (pendingRagIds.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      for (const id of pendingRagIds) {
        try {
          const fresh = await openapiCatalogService.getAdmin(id);
          if (cancelled) return;
          setItems((rows) => {
            let changed = false;
            const next = rows.map((row) => {
              if (
                row.id !== id ||
                (row.ragStatus === fresh.ragStatus &&
                  row.analyzedAt === fresh.analyzedAt &&
                  row.ragError === fresh.ragError)
              ) {
                return row;
              }
              changed = true;
              return { ...row, ...fresh };
            });
            return changed ? next : rows;
          });
          setLive((current) =>
            current?.id === id &&
            (current.ragStatus !== fresh.ragStatus ||
              current.analyzedAt !== fresh.analyzedAt ||
              current.ragError !== fresh.ragError)
              ? fresh
              : current
          );
        } catch {
          // Keep the last status; the next tick retries.
        }
      }
    };
    const timer = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pendingRagIds]);

  const loadQueryHistory = useCallback(async (catalogId: string, page = 0) => {
    setQueryHistoryLoading(true);
    setQueryHistoryPage(page);
    try {
      const [calls, catalog] = await Promise.all([
        openapiCatalogService.queryCalls(catalogId, page),
        openapiCatalogService.getAdmin(catalogId),
      ]);
      setQueryHistory(calls.content ?? []);
      setQueryHistoryTotal(calls.totalElements ?? 0);
      setLive(catalog);
    } catch {
      setQueryHistory([]);
      setQueryHistoryTotal(0);
    } finally {
      setQueryHistoryLoading(false);
    }
  }, []);

  const loadOperations = useCallback(async (catalogId: string) => {
    setOpsLoading(true);
    try {
      setOperations(await openapiCatalogService.listOperations(catalogId));
    } catch {
      setOperations([]);
    } finally {
      setOpsLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadList()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [loadList]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const hay = `${item.name} ${item.baseUrl} ${item.openapiDocUrl}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const openCreate = () => {
    setDraft(emptyDraft());
    setSecrets(emptySecrets());
    setLive(null);
    setOperations([]);
    setQueryHistory([]);
    setQueryHistoryPage(0);
    setQueryHistoryTotal(0);
    setAuthDialogOpen(false);
    setMessage(null);
    setError(null);
    setMode("editor");
  };

  const openEdit = (catalog: OpenApiCatalog) => {
    setDraft(draftFromCatalog(catalog));
    setSecrets(secretsFromCatalog(catalog));
    setLive(catalog);
    setAuthDialogOpen(false);
    setMessage(null);
    setError(null);
    setMode("editor");
    void loadOperations(catalog.id);
    void loadQueryHistory(catalog.id);
  };

  const backToBrowse = () => {
    setMode("browse");
    setDraft(emptyDraft());
    setSecrets(emptySecrets());
    setLive(null);
    setOperations([]);
    setQueryHistory([]);
    setQueryHistoryPage(0);
    setQueryHistoryTotal(0);
    setAuthDialogOpen(false);
    setMessage(null);
    setError(null);
  };

  const patchDraft = (patch: Partial<CatalogDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const openAddAuth = () => {
    setAuthDialogOpen(true);
  };

  const applyAuth = (next: AuthDraft) => {
    const authType =
      next.authType === DEFAULT_AUTH || !AUTH_FEATURE_TYPES.includes(next.authType)
        ? "FIXED_TOKEN"
        : next.authType;
    patchDraft({ ...next, authType });
    setAuthDialogOpen(false);
  };

  const removeAuth = async () => {
    try {
      await confirmDelete({
        itemName: t("openapiCatalogs.sectionAuth"),
        titleKey: "openapiCatalogs.authRemoveTitle",
        descriptionKey: "openapiCatalogs.authRemoveDescription",
      });
      patchDraft({
        ...emptyAuthDraft(DEFAULT_AUTH),
        authType: DEFAULT_AUTH,
      });
      setSecrets((prev) => ({
        ...prev,
        fixedTokenSet: false,
        basicPasswordSet: false,
        oauthClientSecretSet: false,
        oauthPasswordSet: false,
      }));
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const buildPayload = (): OpenApiCatalogSavePayload => {
    const authType = draft.authType || DEFAULT_AUTH;
    const oauthGrant = authType === "OAUTH" ? draft.oauthGrant || "CLIENT_CREDENTIALS" : null;
    return {
      name: draft.name.trim(),
      openapiDocUrl: draft.openapiDocUrl.trim(),
      baseUrl: draft.baseUrl.trim(),
      enabled: !!draft.enabled,
      vpnProfileId: draft.vpnProfileId || "",
      authType,
      oauthGrant,
      basicUsername: authType === "BASIC" ? draft.basicUsername?.trim() || null : null,
      oauthTokenUrl: authType === "OAUTH" ? draft.oauthTokenUrl?.trim() || null : null,
      oauthClientId: authType === "OAUTH" ? draft.oauthClientId?.trim() || null : null,
      oauthScopes: authType === "OAUTH" ? draft.oauthScopes?.trim() || null : null,
      oauthUsername:
        authType === "OAUTH" && oauthGrant === "PASSWORD"
          ? draft.oauthUsername?.trim() || null
          : null,
      fixedToken: authType === "FIXED_TOKEN" ? draft.fixedToken || null : null,
      basicPassword: authType === "BASIC" ? draft.basicPassword || null : null,
      oauthClientSecret:
        authType === "OAUTH" && oauthGrant !== "PASSWORD" ? draft.oauthClientSecret || null : null,
      oauthPassword:
        authType === "OAUTH" && oauthGrant === "PASSWORD" ? draft.oauthPassword || null : null,
      autoDisableEnabled: !!draft.autoDisableEnabled,
      autoDisableMinSuccessPercent: (() => {
        const n = Number(draft.minSuccessPercentText);
        return Number.isFinite(n) && draft.minSuccessPercentText.trim()
          ? Math.min(100, Math.max(0, Math.round(n)))
          : 80;
      })(),
      autoDisableMinCalls: (() => {
        const n = Number(draft.minCallsText);
        return Number.isFinite(n) && n > 0 ? Math.round(n) : 10;
      })(),
    };
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const body = buildPayload();
      if (draft.id) {
        const saved = await openapiCatalogService.update(draft.id, body);
        setMessage(t("openapiCatalogs.updated"));
        setDraft(draftFromCatalog(saved));
        setSecrets(secretsFromCatalog(saved));
        setLive(saved);
        await loadOperations(saved.id);
        await loadQueryHistory(saved.id);
      } else {
        const created = await openapiCatalogService.create(body);
        setMessage(t("openapiCatalogs.created"));
        setDraft(draftFromCatalog(created));
        setSecrets(secretsFromCatalog(created));
        setLive(created);
        await loadOperations(created.id);
        await loadQueryHistory(created.id);
      }
      await loadList();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = async (title: string, description: string) => {
    await confirm({
      title,
      description,
      confirmationText: t("common.yes"),
      cancellationText: t("common.cancel"),
    });
  };

  const refreshSnapshot = async () => {
    if (!draft.id) return;
    try {
      await confirmAction(t("openapiCatalogs.refresh"), t("openapiCatalogs.refreshConfirm"));
    } catch {
      return;
    }
    setRefreshing(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await openapiCatalogService.refresh(draft.id);
      setLive(saved);
      setDraft(draftFromCatalog(saved));
      setSecrets(secretsFromCatalog(saved));
      setMessage(t("openapiCatalogs.refreshSuccess"));
      await Promise.all([loadList(), loadOperations(saved.id)]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  const reanalyze = async () => {
    if (!draft.id) return;
    try {
      await confirmAction(t("openapiCatalogs.reanalyze"), t("openapiCatalogs.reanalyzeConfirm"));
    } catch {
      return;
    }
    setAnalyzing(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await openapiCatalogService.analyzeRag(draft.id);
      setLive(saved);
      setMessage(t("openapiCatalogs.reanalyzeSuccess"));
      await loadList();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const openTagAnalyses = async () => {
    if (!draft.id) return;
    setTagAnalysesOpen(true);
    setTagAnalysesLoading(true);
    setTagAnalyses([]);
    setSelectedTag("");
    try {
      const rows = await openapiCatalogService.tagAnalyses(draft.id);
      setTagAnalyses(rows);
      setSelectedTag(rows[0]?.tag || "");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setTagAnalysesOpen(false);
    } finally {
      setTagAnalysesLoading(false);
    }
  };

  const selectedTagAnalysis = tagAnalyses.find((row) => row.tag === selectedTag);

  const removeCatalog = async (catalog: OpenApiCatalog) => {
    try {
      await confirmDelete({ itemName: catalog.name });
      await openapiCatalogService.remove(catalog.id);
      if (draft.id === catalog.id) backToBrowse();
      await loadList();
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const authDialogInitial = useMemo((): AuthDraft => {
    if (authAttached) {
      return {
        authType: draft.authType,
        oauthGrant: draft.oauthGrant,
        basicUsername: draft.basicUsername,
        oauthTokenUrl: draft.oauthTokenUrl,
        oauthClientId: draft.oauthClientId,
        oauthScopes: draft.oauthScopes,
        oauthUsername: draft.oauthUsername,
        fixedToken: draft.fixedToken,
        basicPassword: draft.basicPassword,
        oauthClientSecret: draft.oauthClientSecret,
        oauthPassword: draft.oauthPassword,
      };
    }
    return emptyAuthDraft("FIXED_TOKEN");
  }, [authAttached, draft]);

  if (mode === "editor") {
    return (
      <PageListContainer>
        <PageCommandBar
          leading={
            <AppIconButton
              title={t("openapiCatalogs.backToList")}
              onClick={backToBrowse}
              disabled={busy}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
            </AppIconButton>
          }
          title={draft.id ? t("openapiCatalogs.edit") : t("openapiCatalogs.create")}
          subtitle={draft.name?.trim() || t("openapiCatalogs.catalogSection")}
          actions={
            <ResponsiveActions
              items={[
                {
                  key: "refresh",
                  label: refreshing ? t("openapiCatalogs.refreshing") : t("openapiCatalogs.refresh"),
                  icon: "refresh",
                  onClick: () => void refreshSnapshot(),
                  disabled: busy,
                  hidden: !draft.id,
                },
                {
                  key: "reanalyze",
                  label: analyzing ? t("openapiCatalogs.reanalyzing") : t("openapiCatalogs.reanalyze"),
                  icon: "auto_awesome",
                  onClick: () => void reanalyze(),
                  disabled: busy,
                  hidden: !draft.id,
                },
                {
                  key: "delete",
                  label: t("common.delete"),
                  icon: "delete_outline",
                  color: "error",
                  onClick: () => live && void removeCatalog(live),
                  disabled: busy || !live,
                  hidden: !draft.id,
                },
                {
                  key: "save",
                  label: saving
                    ? t("common.saving")
                    : draft.id
                      ? t("common.save")
                      : t("openapiCatalogs.createButton"),
                  onClick: () => void save(),
                  disabled: busy,
                  primary: true,
                },
              ]}
            />
          }
        />

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <PageSectionCard
          title={t("openapiCatalogs.sectionConnection")}
          description={t("openapiCatalogs.catalogSection")}
          actions={
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t("openapiCatalogs.enabled")}
              </Typography>
              <Switch
                size="small"
                checked={draft.enabled}
                onChange={(e) => patchDraft({ enabled: e.target.checked })}
                disabled={busy}
                inputProps={{ "aria-label": t("openapiCatalogs.enabled") }}
              />
            </Stack>
          }
        >
          <AppFormGrid spacing="compact">
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("openapiCatalogs.name")}
                value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
                required
                autoFocus
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={t("openapiCatalogs.openapiDocUrl")}
                value={draft.openapiDocUrl}
                onChange={(e) => patchDraft({ openapiDocUrl: e.target.value })}
                required
                helperText={t("openapiCatalogs.openapiDocUrlHint")}
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField
                label={t("openapiCatalogs.baseUrl")}
                value={draft.baseUrl}
                onChange={(e) => patchDraft({ baseUrl: e.target.value })}
                required
                helperText={t("openapiCatalogs.baseUrlHint")}
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <VpnProfileSelect
                value={draft.vpnProfileId}
                currentName={draft.vpnProfileName}
                onChange={(vpnProfileId) => patchDraft({ vpnProfileId })}
              />
            </AppFormField>
          </AppFormGrid>

          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t("openapiCatalogs.sectionAuth")}
            </Typography>
            {authAttached ? (
              <Box sx={softSurfaceSx}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" minWidth={0}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "action.selected",
                        color: "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      <SecurityOutlinedIcon fontSize="small" />
                    </Box>
                    <Box minWidth={0}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {authTypeLabel(t, draft.authType)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {authSummary(t, draft)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.75} flexShrink={0}>
                    <AppButton
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setAuthDialogOpen(true)}
                      disabled={busy}
                    >
                      {t("openapiCatalogs.authChange")}
                    </AppButton>
                    <AppButton
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineOutlinedIcon />}
                      onClick={() => void removeAuth()}
                      disabled={busy}
                    >
                      {t("openapiCatalogs.authRemove")}
                    </AppButton>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ ...softSurfaceSx, bgcolor: "action.hover" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" minWidth={0}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "background.paper",
                        color: "text.secondary",
                        flexShrink: 0,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <KeyOutlinedIcon fontSize="small" />
                    </Box>
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t("openapiCatalogs.authFeatureEmptyTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {t("openapiCatalogs.authFeatureDefaultHint")}
                      </Typography>
                    </Box>
                  </Stack>
                  <AppButton
                    size="small"
                    variant="contained"
                    startIcon={<AddOutlinedIcon />}
                    onClick={openAddAuth}
                    disabled={busy}
                  >
                    {t("openapiCatalogs.authAdd")}
                  </AppButton>
                </Stack>
              </Box>
            )}
          </Box>
        </PageSectionCard>

        <PageSectionCard>
          <ResourceQueryStatsPanel
            saved={!!draft.id}
            stats={{
              queryCallCount: live?.queryCallCount ?? 0,
              querySuccessCount: live?.querySuccessCount ?? 0,
              queryFailureCount: live?.queryFailureCount ?? 0,
              autoDisabledAt: live?.autoDisabledAt ?? null,
            }}
            history={queryHistory}
            historyPage={queryHistoryPage}
            historyTotal={queryHistoryTotal}
            onHistoryPageChange={(page) => {
              if (draft.id) void loadQueryHistory(draft.id, page);
            }}
            historyLoading={queryHistoryLoading}
            resetting={resettingStats}
            onReset={
              draft.id
                ? async () => {
                    if (!draft.id) return;
                    setResettingStats(true);
                    setError(null);
                    try {
                      const updated = await openapiCatalogService.resetQueryStats(draft.id);
                      setLive(updated);
                      setMessage(t("resourceQueryStats.resetOk"));
                      await loadList();
                    } catch (e: unknown) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setResettingStats(false);
                    }
                  }
                : undefined
            }
            autoDisableEnabled={!!draft.autoDisableEnabled}
            minSuccessPercent={draft.minSuccessPercentText}
            minCalls={draft.minCallsText}
            onAutoDisableChange={(patch) =>
              patchDraft({
                ...(patch.autoDisableEnabled != null ? { autoDisableEnabled: patch.autoDisableEnabled } : {}),
                ...(patch.minSuccessPercent != null ? { minSuccessPercentText: patch.minSuccessPercent } : {}),
                ...(patch.minCalls != null ? { minCallsText: patch.minCalls } : {}),
              })
            }
          />
        </PageSectionCard>

        {draft.id && live && (
          <PageSectionCard title={t("openapiCatalogs.operationsList")}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="stretch"
                sx={{ "& > *": { display: "flex" } }}
              >
                <Box sx={{ minWidth: 120, flex: "1 1 120px", minHeight: 88 }}>
                  <PageStatCard
                    centered
                    label={t("openapiCatalogs.tagCountLabel")}
                    value={live.tagCount ?? 0}
                  />
                </Box>
                <Box sx={{ minWidth: 120, flex: "1 1 120px", minHeight: 88 }}>
                  <PageStatCard
                    centered
                    label={t("openapiCatalogs.operationCountLabel")}
                    value={live.operationCount ?? 0}
                  />
                </Box>
                <Box sx={{ minWidth: 160, flex: "1 1 160px", minHeight: 88 }}>
                  <PageStatCard centered label={t("openapiCatalogs.snapshot")}>
                    <Chip
                      size="small"
                      color={snapshotChipColor(live)}
                      label={snapshotLabel(t, live)}
                    />
                  </PageStatCard>
                </Box>
                <Box sx={{ minWidth: 160, flex: "1 1 160px", minHeight: 88 }}>
                  <PageStatCard centered label={t("openapiCatalogs.ragStatus")}>
                    <Chip
                      size="small"
                      color={ragChipColor(live.ragStatus)}
                      label={ragLabel(t, live.ragStatus)}
                    />
                  </PageStatCard>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t("openapiCatalogs.snapshotFetchedAt")}:{" "}
                  {live.snapshotFetchedAt
                    ? new Date(live.snapshotFetchedAt).toLocaleString()
                    : "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("openapiCatalogs.analyzedAt")}:{" "}
                  {live.analyzedAt ? new Date(live.analyzedAt).toLocaleString() : "—"}
                </Typography>
              </Stack>

              {live.ragError && (
                <Alert severity="error">
                  <Typography variant="subtitle2">{t("openapiCatalogs.ragError")}</Typography>
                  {live.ragError}
                </Alert>
              )}
              {live.lastError && (
                <Alert severity="warning">
                  <Typography variant="subtitle2">{t("openapiCatalogs.lastError")}</Typography>
                  {live.lastError}
                </Alert>
              )}

              {opsLoading ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress size={28} />
                </Stack>
              ) : operations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("openapiCatalogs.operationsEmpty")}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="flex-end">
                    <AppButton
                      size="small"
                      variant="outlined"
                      disabled={!draft.id}
                      onClick={() => void openTagAnalyses()}
                    >
                      {t("openapiCatalogs.tagAnalyses")}
                    </AppButton>
                  </Stack>
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("openapiCatalogs.opMethod")}</TableCell>
                        <TableCell>{t("openapiCatalogs.opPath")}</TableCell>
                        <TableCell>{t("openapiCatalogs.opOperationId")}</TableCell>
                        <TableCell>{t("openapiCatalogs.opSummary")}</TableCell>
                        <TableCell>{t("openapiCatalogs.opTags")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operations.map((op) => (
                        <TableRow key={`${op.method}:${op.path}:${op.operationId || ""}`}>
                          <TableCell>
                            <Chip size="small" label={op.method} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {op.path}
                            </Typography>
                          </TableCell>
                          <TableCell>{op.operationId || "—"}</TableCell>
                          <TableCell>{op.summary || "—"}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                              {(op.tags || []).map((tag) => (
                                <Chip key={tag} size="small" label={tag} />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Stack>
              )}
            </Stack>
          </PageSectionCard>
        )}

        <AuthorizationDialog
          open={authDialogOpen}
          editing={authAttached}
          initial={authDialogInitial}
          secrets={secrets}
          catalogId={draft.id}
          onClose={() => setAuthDialogOpen(false)}
          onApply={applyAuth}
        />
        <AppDialog open={tagAnalysesOpen} onClose={() => setTagAnalysesOpen(false)} maxWidth="md" fullWidth>
          <AppDialogTitle>{t("openapiCatalogs.tagAnalyses")}</AppDialogTitle>
          <AppDialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("openapiCatalogs.tagAnalysesHint")}
            </Typography>
            {tagAnalysesLoading ? (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={28} />
              </Stack>
            ) : tagAnalyses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("openapiCatalogs.tagAnalysesEmpty")}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                <AppAutocomplete<string, false, true>
                  label={t("openapiCatalogs.opTags")}
                  options={tagAnalyses.map((row) => row.tag)}
                  value={selectedTag}
                  disableClearable
                  onChange={(_, value) => setSelectedTag(value)}
                />
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    maxHeight: 420,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    fontSize: 13,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  {selectedTagAnalysis?.markdown || t("openapiCatalogs.tagAnalysesEmpty")}
                </Box>
              </Stack>
            )}
          </AppDialogContent>
          <AppDialogActions>
            <AppDialogCancelButton onClick={() => setTagAnalysesOpen(false)}>
              {t("common.close")}
            </AppDialogCancelButton>
          </AppDialogActions>
        </AppDialog>
      </PageListContainer>
    );
  }

  return (
    <PageListContainer>
      <PageHeader
        title={t("openapiCatalogs.title")}
        subtitle={t("openapiCatalogs.subtitle")}
        actions={
          <ResponsiveActions
            items={[
              {
                key: "create",
                label: t("openapiCatalogs.newCatalog"),
                icon: "add",
                primary: true,
                disabled: loading,
                onClick: openCreate,
              },
            ]}
          />
        }
      />

      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxOpenApiCatalogs != null}>
          {t("packageLimits.openApiCatalogs", {
            count: formatLimitValue(packageLimits.maxOpenApiCatalogs, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <PageSectionCard title={t("openapiCatalogs.listTitle")}>
        <Stack spacing={1.5}>
          <AppTextField
            label={t("common.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("openapiCatalogs.searchPlaceholder")}
            fullWidth
          />

          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress size={32} />
            </Stack>
          ) : filtered.length === 0 ? (
            <PageEmptyState
              message={items.length === 0 ? t("openapiCatalogs.empty") : t("openapiCatalogs.noMatches")}
              actionLabel={items.length === 0 ? t("openapiCatalogs.createButton") : undefined}
              onAction={items.length === 0 ? openCreate : undefined}
              actionIcon={items.length === 0 ? <AddOutlinedIcon /> : undefined}
            />
          ) : (
            <Stack spacing={1}>
              {filtered.map((item) => (
                <Box
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEdit(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openEdit(item);
                    }
                  }}
                  sx={catalogTileSx}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box minWidth={0} flex={1}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {item.name}
                        </Typography>
                        {!item.enabled && (
                          <Chip size="small" label={t("common.no")} variant="outlined" />
                        )}
                        {isAuthFeatureAttached(item.authType) && (
                          <Chip
                            size="small"
                            icon={<SecurityOutlinedIcon />}
                            label={authTypeLabel(t, (item.authType || DEFAULT_AUTH) as OpenApiCatalogAuthType)}
                            variant="outlined"
                          />
                        )}
                        {item.vpnProfileName && (
                          <Chip size="small" label={`VPN: ${item.vpnProfileName}`} variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                        {item.baseUrl}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {t("openapiCatalogs.statsShort", {
                          tags: item.tagCount ?? 0,
                          operations: item.operationCount ?? 0,
                        })}
                        {` · ${formatQueryStatsCaption(item)}`}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                      <Chip
                        size="small"
                        color={snapshotChipColor(item)}
                        label={snapshotLabel(t, item)}
                      />
                      <Chip
                        size="small"
                        color={ragChipColor(item.ragStatus)}
                        label={ragLabel(t, item.ragStatus)}
                      />
                      <AppIconButton
                        size="small"
                        aria-label={t("common.delete")}
                        onClick={(e) => {
                          e.stopPropagation();
                          void removeCatalog(item);
                        }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </AppIconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </PageSectionCard>
    </PageListContainer>
  );
}
